import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles, CalendarClock, TrendingUp, Moon, FileText, BookOpen,
    MessageCircle, ArrowRight, Sun, Compass, CheckCircle2, HeartHandshake,
} from "lucide-react";

/* MOCKUP — Effortless Retention home experience.
   Static data only. Demonstrates the philosophy: every visit answers
   "What can Memory Lane do for me right now?" — Lane has already done
   the work before the caregiver arrives. No streaks, no guilt, no
   manufactured activity. Branding + core navigation unchanged. */

const INK = "#12776F";
const SOFT = "#4C5551";

function Eyebrow({ children }) {
    return <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: "#8AA6AD" }}>{children}</div>;
}

function BriefCard({ icon: Icon, eyebrow, title, body, action, tone = "white", testid }) {
    const bg = tone === "teal" ? "bg-[#12776F] text-white" : tone === "sky" ? "bg-[#E6F0F2]" : "bg-white";
    return (
        <div className={`rounded-3xl p-6 shadow-[0_10px_30px_-18px_rgba(15,76,92,0.35)] ${bg}`} data-testid={testid}>
            <div className="flex items-start gap-4">
                <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${tone === "teal" ? "bg-white/15" : "bg-[#12776F]/8"}`}>
                    <Icon size={19} strokeWidth={1.7} className={tone === "teal" ? "text-[#E3B562]" : "text-[#12776F]"} />
                </div>
                <div className="min-w-0">
                    <div className={`text-[11px] font-semibold tracking-[0.14em] uppercase ${tone === "teal" ? "text-[#E3B562]" : "text-[#8AA6AD]"}`}>{eyebrow}</div>
                    <h3 className={`ml-v2-serif text-lg font-semibold mt-1 ${tone === "teal" ? "!text-white" : ""}`} style={tone === "teal" ? {} : { color: INK }}>{title}</h3>
                    <p className={`text-sm mt-1.5 leading-relaxed ${tone === "teal" ? "text-white/80" : "text-[#4C5551]"}`}>{body}</p>
                    {action && (
                        <button className={`mt-3 inline-flex items-center gap-1.5 text-sm font-semibold rounded-2xl px-4 py-2 transition-colors ${tone === "teal" ? "bg-[#E3B562] text-[#12776F] hover:bg-[#edc37a]" : "bg-[#12776F] text-white hover:bg-[#14607a]"}`}>
                            {action} <ArrowRight size={14} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const SCENARIOS = {
    a: {
        label: "Back after 6 days",
        greetIcon: Sun,
        greeting: "Welcome back, Jordan.",
        sub: "While you were away, Lane kept watch. Here's what matters.",
        cards: [
            { icon: TrendingUp, eyebrow: "Since your last visit", title: "Rosa's sleep is settling.", body: "You logged restless nights three times two weeks ago — nothing since. Whatever you changed seems to be working. Lane saved the details in the timeline in case the doctor asks.", action: "See the pattern", tone: "white", testid: "mockup-card-changed" },
            { icon: CalendarClock, eyebrow: "Coming up — Thursday", title: "Dr. Patel visit: your summary is already drafted.", body: "Lane pulled together the last month — the sleep change, the new medication timing, and your 2 saved questions. Review it in one minute, or bring it as is.", action: "Review visit summary", tone: "teal", testid: "mockup-card-appointment" },
            { icon: BookOpen, eyebrow: "For where you are now", title: "One reading, if you want it.", body: "\u201CWhen questions repeat: what helps and why.\u201D Picked because of what you've been logging — skip it freely, it will stay here.", action: null, tone: "sky", testid: "mockup-card-resource" },
        ],
        quiet: "Everything else is exactly where it always is — Roadmap, Journey, and Lane in the navigation above.",
    },
    b: {
        label: "Appointment in 2 days",
        greetIcon: CalendarClock,
        greeting: "Good morning, Jordan.",
        sub: "Thursday's appointment is the one thing worth a look today. Lane has prepared it.",
        cards: [
            { icon: FileText, eyebrow: "Prepared for Thursday", title: "Your Care Team Summary is ready.", body: "One page: what changed since the last visit, current medications, the sleep pattern, and the fall concern from March — organized the way clinicians read. Nothing for you to write.", action: "Open the summary", tone: "teal", testid: "mockup-card-summary" },
            { icon: MessageCircle, eyebrow: "Your saved questions", title: "3 questions, saved as they came to you.", body: "\u201CIs the new dose causing the afternoon drowsiness?\u201D + 2 more. They're printed on the summary — you won't have to remember them in the room.", action: "See questions", tone: "white", testid: "mockup-card-questions" },
        ],
        quiet: "Nothing else needs you today. After the visit, tell Lane how it went — or don't. It will be here.",
    },
    c: {
        label: "A quiet week",
        greetIcon: Compass,
        greeting: "Good evening, Jordan.",
        sub: "A steady week. Nothing needs your attention — and that's a good thing.",
        cards: [
            { icon: CheckCircle2, eyebrow: "Lane checked", title: "No changes worth flagging.", body: "No new patterns in what you've shared, no upcoming appointments, no follow-ups pending. Memory Lane keeps the whole story safe for the day you need it.", action: null, tone: "sky", testid: "mockup-card-quiet" },
            { icon: HeartHandshake, eyebrow: "A place for you", title: "How are YOU carrying this week?", body: "Only if you feel like it — a one-minute check-in for yourself, never required, never scored against you.", action: "Take a minute", tone: "white", testid: "mockup-card-foryou" },
        ],
        quiet: "We won't manufacture things for you to do. When something changes, this page will change.",
    },
    d: {
        label: "Pattern noticed",
        greetIcon: Moon,
        greeting: "Good morning, Jordan.",
        sub: "You logged evening restlessness again last night. Lane looked back so you don't have to.",
        cards: [
            { icon: Sparkles, eyebrow: "What helped before", title: "In April, earlier dinners helped within a week.", body: "This looks like the pattern from April 12–19. Back then you noted \u201Cmoved dinner to 5:30 — calmer nights by Friday.\u201D Worth trying again, or ask Lane what else families try.", action: "See what worked", tone: "teal", testid: "mockup-card-intervention" },
            { icon: TrendingUp, eyebrow: "The pattern", title: "3 restless evenings in 8 days.", body: "Similar timing each night (7–9 pm), often after busier afternoons. Lane added this to the timeline and will include it in the next visit summary automatically.", action: "View timeline", tone: "white", testid: "mockup-card-pattern" },
            { icon: MessageCircle, eyebrow: "Lane remembers", title: "Continue where you left off.", body: "Last week you asked about sundowning. Lane kept the thread — no need to explain everything again.", action: "Continue with Lane", tone: "sky", testid: "mockup-card-thread" },
        ],
        quiet: "One thing to consider today — not ten. Everything else stays quietly in place.",
    },
};

const FLOW = [
    { check: "Appointment within 7 days?", then: "Auto-prepare the Care Team Summary + surface saved questions. This becomes the primary card." },
    { check: "Something changed since last visit?", then: "\u201CSince you were last here\u201D briefing — new patterns, eased patterns, follow-ups Lane completed." },
    { check: "Recent behavior pattern detected?", then: "Look back through the caregiver's own history first — surface the intervention that helped before, then offer Lane." },
    { check: "Care event (hospitalization, transition)?", then: "Update the Roadmap automatically and present next steps — never a blank form." },
    { check: "Stage-relevant resource unread?", then: "At most ONE gentle suggestion, clearly optional." },
    { check: "Nothing meaningful changed?", then: "Say so, calmly. A quiet page is a feature — never manufacture activity." },
];

export default function HomePreviewMockup() {
    const [sc, setSc] = useState("a");
    const s = SCENARIOS[sc];
    const G = s.greetIcon;
    return (
        <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #F5FAFB 0%, #EDF4F5 100%)" }} data-testid="mockup-home">
            {/* Mockup banner */}
            <div className="bg-[#12776F] text-white text-center text-xs py-2 px-4">
                MOCKUP · Effortless Retention home concept · static example data — not your live dashboard · <Link to="/dashboard" className="underline text-[#E3B562]">back to the real app</Link>
            </div>

            <div className="max-w-3xl mx-auto px-5 py-10">
                {/* Scenario switcher */}
                <Eyebrow>Choose a moment in a caregiver's life</Eyebrow>
                <div className="mt-2 mb-10 flex flex-wrap gap-2" data-testid="mockup-scenario-switcher">
                    {Object.entries(SCENARIOS).map(([k, v]) => (
                        <button key={k} onClick={() => setSc(k)} data-testid={`mockup-scenario-${k}`}
                            className={`px-4 py-2 rounded-2xl text-sm font-semibold transition-colors ${sc === k ? "bg-[#12776F] text-white" : "bg-white text-[#12776F] hover:bg-[#E6F0F2]"}`}>
                            {v.label}
                        </button>
                    ))}
                </div>

                {/* The home itself */}
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-[#E3B562]/25 flex items-center justify-center"><G size={20} className="text-[#b8842e]" /></div>
                    <div>
                        <h1 className="ml-v2-serif text-3xl font-semibold" style={{ color: INK }} data-testid="mockup-greeting">{s.greeting}</h1>
                        <p className="text-sm mt-0.5" style={{ color: SOFT }}>{s.sub}</p>
                    </div>
                </div>

                <div className="mt-7 space-y-4" data-testid="mockup-brief-stack">
                    {s.cards.map((c) => <BriefCard key={c.testid} {...c} />)}
                </div>

                <p className="mt-8 text-sm italic text-center" style={{ color: "#8AA6AD" }} data-testid="mockup-quiet-note">{s.quiet}</p>

                {/* ---- User flow ---- */}
                <div className="mt-16 pt-10 border-t border-[#12776F]/10">
                    <Eyebrow>How the home decides — priority order, on every visit</Eyebrow>
                    <div className="mt-4 space-y-3" data-testid="mockup-flow">
                        {FLOW.map((f, i) => (
                            <div key={i} className="flex items-start gap-3 bg-white rounded-2xl p-4">
                                <div className="shrink-0 h-7 w-7 rounded-full bg-[#12776F] text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                                <div>
                                    <div className="text-sm font-semibold" style={{ color: INK }}>{f.check}</div>
                                    <div className="text-sm mt-0.5" style={{ color: SOFT }}>→ {f.then}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 bg-[#E6F0F2] rounded-3xl p-6" data-testid="mockup-principles">
                        <h3 className="ml-v2-serif text-lg font-semibold" style={{ color: INK }}>What this deliberately removes</h3>
                        <ul className="mt-3 space-y-1.5 text-sm" style={{ color: SOFT }}>
                            <li>✕ No streaks, badges, or daily check-in requirements</li>
                            <li>✕ No red notification counts or guilt for time away</li>
                            <li>✕ No re-asking for information the caregiver already gave</li>
                            <li>✕ No manufactured activity when nothing changed</li>
                            <li>✓ Max 3 cards, one primary action, quiet by default</li>
                            <li>✓ Core navigation, branding, and all existing pages unchanged</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
